import {
    Competition,
    Game,
    Team
} from "../services/mexfbaApi";

function parseDate(date?: string): Date | null {
    if (!date) {
        return null;
    }

    const normalizedDate =
        date.length === 10 ? `${date}T12:00:00` : date;

    const parsedDate = new Date(normalizedDate);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function isCompletedGame(game: Game): boolean {
    const gameDate = parseDate(game.fecha);

    if (!gameDate) {
        return false;
    }

    const today = new Date();

    today.setHours(0, 0, 0, 0);

    if (gameDate >= today) {
        return false;
    }

    const localScore = Number(game.resLocal);
    const visitorScore = Number(game.resVisita);

    return (
        game.resLocal !== "" &&
        game.resLocal !== null &&
        game.resLocal !== undefined &&
        game.resVisita !== "" &&
        game.resVisita !== null &&
        game.resVisita !== undefined &&
        Number.isFinite(localScore) &&
        Number.isFinite(visitorScore)
    );
}

function getInitialRating(team: Team): number {
    const rankedTeam = team as Team & {
        puntos?: string | number;
    };

    const originalPosition = Number(rankedTeam.puntos);

    /*
        Conserva la calibración histórica del ranking web.
        Los valores bajos de "puntos" representan posiciones iniciales.
    */
    if (Number.isFinite(originalPosition)) {
        if (originalPosition < 6) {
            return 1200 - (originalPosition - 1) * 10;
        }

        return 1160 - (originalPosition - 1) * 5;
    }

    return 1100;
}


function getCompetitionWeight(
    competition: Competition | undefined,
    phase?: string
): number {
    const baseWeight = Number(competition?.puntos) || 0;

    /*
        En el ranking web original, la fase "1" recibe cinco puntos
        adicionales por representar el juego de campeonato.
    */
    if (phase === "1") {
        return baseWeight + 5;
    }

    return baseWeight;
}

function getExpectedResult(
    teamRating: number,
    opponentRating: number
): number {
    return 1 / (Math.pow(10, -(teamRating - opponentRating) / 600) + 1);
}

function getRealResult(
    teamScore: number,
    opponentScore: number
): number {
    if (teamScore > opponentScore) {
        return 1;
    }

    if (teamScore < opponentScore) {
        return 0;
    }

    /*
        Corrige un problema del código anterior:
        un empate no debe contar como victoria para ambos equipos.
    */
    return 0.5;
}

/*
    Sólo estas dos competencias usan ranking interno para determinar
    mayor relevancia en los partidos destacados.
*/
export function usesInternalRanking(competitionName?: string): boolean {
    if (!competitionName) {
        return false;
    }

    return /^(14 Grandes|Nacional)\s+\d{4}$/i.test(
        competitionName.trim()
    );
}

/*
    Genera una puntuación Elo interna usando todos los partidos históricos
    terminados disponibles en partidos.json.
*/
export function createInternalRatings(
    teams: Team[],
    competitions: Competition[],
    games: Game[]
): Map<string, number> {
    const ratings = new Map<string, number>();

    const competitionMap = new Map(
        competitions.map(function (competition) {
            return [String(competition.id), competition];
        })
    );

    teams.forEach(function (team) {
        ratings.set(String(team.id), getInitialRating(team));
    });

    const completedGames = games
        .filter(isCompletedGame)
        .sort(function (firstGame, secondGame) {
            const firstDate = parseDate(firstGame.fecha)?.getTime() || 0;
            const secondDate = parseDate(secondGame.fecha)?.getTime() || 0;

            return firstDate - secondDate;
        });

    completedGames.forEach(function (game) {
        const localRating = ratings.get(String(game.id_local));
        const visitorRating = ratings.get(String(game.id_visita));

        if (
            localRating === undefined ||
            visitorRating === undefined
        ) {
            return;
        }

        const weight = getCompetitionWeight(
            competitionMap.get(String(game.id_comp)),
            game.partido
        );

        /*
            Si una competencia no tiene peso para ranking,
            sus partidos no alteran las puntuaciones.
        */
        if (weight <= 0) {
            return;
        }

        const localScore = Number(game.resLocal);
        const visitorScore = Number(game.resVisita);

        const localExpected = getExpectedResult(
            localRating,
            visitorRating
        );

        const visitorExpected = getExpectedResult(
            visitorRating,
            localRating
        );

        const localResult = getRealResult(localScore, visitorScore);
        const visitorResult = getRealResult(visitorScore, localScore);

        const localChange = weight * (localResult - localExpected);
        const visitorChange = weight * (visitorResult - visitorExpected);

        ratings.set(
            String(game.id_local),
            localRating + localChange
        );

        ratings.set(
            String(game.id_visita),
            visitorRating + visitorChange
        );
    });

    return ratings;
}
